<template>
  <BaseWidget 
    title="Plan revenue change"
    :show-info-icon="true"
    info-tooltip="Revenue changes across your plans within the selected date range."
    :info-tooltip-link="{ url: 'https://support.optixapp.com/en/articles/2056112-what-is-a-plan', text: 'Learn about Plans' }"
    :edit-mode="editMode"
    :widget-id="props.widgetId"
    :row-span="rowSpan"
    :col-span="colSpan"
    :date-range="dateRange"
    :past-only="true"
    @update:date-range="handleDateRangeChange"
    @remove-widget="$emit('remove-widget', props.widgetId)"
  >
    <div
      v-if="loading"
      class="loading-state"
    >
      <SkeletonLoader
        type="widget"
        :show-table="true"
        :compact="focused"
      />
    </div>
    <div
      v-else-if="error"
      class="error-state"
    >
      <RateLimitError v-if="isRateLimitError(error)" />
      <span v-else>Error: {{ error.message }}</span>
    </div>
    <div
      v-else
      class="widget-content-wrapper"
    >
      <div class="total-revenue-metric">
        <div class="metric-value-row">
          <div class="metric-value">
            {{ totalRevenueFormatted }}
          </div>
          <ComparisonChip 
            :change="changePercentage" 
            :previous-value="previousTotalRevenueFormatted"
            :previous-period-dates="previousTimestamps"
            :date-range-preset="dateRange"
            label="Plan revenue change"
          />
        </div>
        <div class="metric-label">
          {{ formattedDateRange }}
        </div>
      </div>
      <div
        v-if="!focused"
        class="plans-table"
      >
        <div class="table-header">
          <div class="table-header-cell plan-cell">
            Plan name
          </div>
          <div class="table-header-cell period-cell">
            Revenue
          </div>
        </div>
        <div
          v-if="plansWithChange.length === 0"
          class="no-plans"
        >
          No plans found for this period
        </div>
        <div 
          v-for="plan in plansWithChange" 
          :key="plan.id" 
          class="table-row"
          @click="openPlanDetailsGuarded(plan.id)"
        >
          <div class="table-cell plan-cell">
            <div class="plan-info">
              <div class="plan-name">
                {{ plan.name || 'Unknown Plan' }}
              </div>
              <div class="account-name">
                {{ plan.accountName || 'Unknown Account' }}
              </div>
            </div>
          </div>
          <div class="table-cell period-cell">
            <div class="revenue-with-change">
              <span class="revenue-amount">{{ plan.thisPeriodFormatted }}</span>
              <span
                v-if="plan.changeFormatted"
                class="change-indicator"
                :class="plan.changeType"
              >
                {{ plan.changeFormatted }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </BaseWidget>
</template>

<script setup>
import { computed, onUnmounted, ref, watch } from 'vue'
import BaseWidget from './BaseWidget.vue'
import ComparisonChip from './ComparisonChip.vue'
import SkeletonLoader from './SkeletonLoader.vue'
import RateLimitError from './RateLimitError.vue'
import { usePlanRevenueData } from '../composables/useWidgetData.js'
import { DATE_RANGE_LABELS, getPreviousPeriodTimestamps, getDateRangeTimestamps } from '../utils/dateRanges.js'
import { isRateLimitError } from '../utils/errorUtils.js'
import { useTrialModeGuard } from '../composables/useTrialModeGuard.js'

const props = defineProps({
  rowSpan: {
    type: Number,
    default: 1
  },
  colSpan: {
    type: Number,
    default: 1
  },
  editMode: {
    type: Boolean,
    default: false
  },
  props: {
    type: Object,
    default: () => ({})
  },
  widgetId: {
    type: String,
    default: null
  },
  sectionId: {
    type: String,
    default: null
  },
  widgetType: {
    type: String,
    default: null
  },
  focused: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:props', 'remove-widget'])

// Initialize dateRange from props, fallback to default
const dateRange = ref(props.props?.dateRange || 'last_30_days')

const dateRangeLabel = computed(() => {
  return DATE_RANGE_LABELS[dateRange.value] || 'Last 30 days'
})

// Format the actual date range for display
const formattedDateRange = computed(() => {
  const timestamps = getDateRangeTimestamps(dateRange.value)
  const startDate = new Date(timestamps.from_timestamp * 1000)
  const endDate = new Date(timestamps.to_timestamp * 1000)
  
  const startFormatted = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  const endFormatted = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  
  return `${startFormatted} - ${endFormatted}`
})

// Get previous period timestamps for tooltip
const previousTimestamps = computed(() => {
  return getPreviousPeriodTimestamps(dateRange.value)
})

const { data, loading, error, cleanup, refresh } = usePlanRevenueData({
  variables: computed(() => ({ dateRange: dateRange.value })),
  priority: 1
})

// Fetch previous period data for comparison
const { 
  data: previousData, 
  loading: previousLoading, 
  error: previousError, 
  cleanup: previousCleanup 
} = usePlanRevenueData({
  dataKey: 'planRevenue_previous',
  variables: computed(() => {
    const previousTimestamps = getPreviousPeriodTimestamps(dateRange.value)
    return { 
      previousPeriod: true,
      fromDate: previousTimestamps.from_timestamp,
      toDate: previousTimestamps.to_timestamp
    }
  }),
  priority: 1
})

// Helper function to normalize locale format (convert en_CA to en-CA)
const normalizeLocale = (locale) => {
  if (!locale) return 'en-US'
  return locale.replace(/_/g, '-')
}

// Process plans data from analytics query response
const plans = computed(() => {
  if (!data.value?.analyticsQuery?.data || !Array.isArray(data.value.analyticsQuery.data)) {
    return []
  }
  
  const currencySymbol = data.value?.analyticsQuery?.currency_symbol || '$'
  const rawLocale = data.value?.analyticsQuery?.locale || 'en-US'
  const locale = normalizeLocale(rawLocale)
  const currency = data.value?.analyticsQuery?.currency || 'USD'
  
  // The data array contains rows, where each row is an array of values
  // Based on the query structure:
  // - First column: plan name
  // - Second column: account name
  // - Third column: plan price
  // - Fourth column: plan ID
  // - Fifth column: start_timestamp
  // - Sixth column: end_timestamp (null if ongoing)
  
  // Debug: Log the raw data to see what we're getting
  if (process.env.NODE_ENV === 'development') {
  }
  
  return data.value.analyticsQuery.data
    .map((row) => {
      if (!Array.isArray(row) || row.length < 3) {
        return null
      }
      
      const planName = row[0] || 'Unknown Plan'
      const accountName = row[1] || 'Unknown Account'
      // Handle periodRevenue - revenue for this period (proportional)
      let periodRevenue = 0
      if (row[2] !== undefined && row[2] !== null) {
        periodRevenue = typeof row[2] === 'number' ? row[2] : parseFloat(row[2])
        if (isNaN(periodRevenue)) periodRevenue = 0
      }
      const planId = row[3] || planName // Use plan ID if available, otherwise fallback to name
      const startTimestamp = row[4] || null
      const endTimestamp = row[5] || null
      // Full plan price (row[6])
      let fullPrice = 0
      if (row[6] !== undefined && row[6] !== null) {
        fullPrice = typeof row[6] === 'number' ? row[6] : parseFloat(row[6])
        if (isNaN(fullPrice)) fullPrice = 0
      }
      
      // Format period revenue
      let periodRevenueFormatted
      try {
        periodRevenueFormatted = new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(periodRevenue)
      } catch (error) {
        // Fallback to simple formatting if locale is invalid
        console.warn('Invalid locale format, using fallback:', rawLocale, error)
        periodRevenueFormatted = `${currencySymbol}${periodRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      }
      
      return {
        id: planId,
        name: planName,
        accountName: accountName,
        periodRevenue: periodRevenue,
        periodRevenueFormatted: periodRevenueFormatted,
        price: fullPrice, // Full plan price
        start_timestamp: startTimestamp,
        end_timestamp: endTimestamp
      }
    })
    .filter(plan => plan !== null)
})

// Process previous period plans data
const previousPlans = computed(() => {
  if (!previousData.value?.analyticsQuery?.data || !Array.isArray(previousData.value.analyticsQuery.data)) {
    return []
  }
  
  const currencySymbol = previousData.value?.analyticsQuery?.currency_symbol || '$'
  const rawLocale = previousData.value?.analyticsQuery?.locale || 'en-US'
  const locale = normalizeLocale(rawLocale)
  const currency = previousData.value?.analyticsQuery?.currency || 'USD'
  
  return previousData.value.analyticsQuery.data
    .map((row) => {
      if (!Array.isArray(row) || row.length < 3) {
        return null
      }
      
      const planName = row[0] || 'Unknown Plan'
      const accountName = row[1] || 'Unknown Account'
      // Handle periodRevenue - revenue for this period (proportional)
      let periodRevenue = 0
      if (row[2] !== undefined && row[2] !== null) {
        periodRevenue = typeof row[2] === 'number' ? row[2] : parseFloat(row[2])
        if (isNaN(periodRevenue)) periodRevenue = 0
      }
      const planId = row[3] || planName
      const startTimestamp = row[4] || null
      const endTimestamp = row[5] || null
      // Full plan price (row[6])
      let fullPrice = 0
      if (row[6] !== undefined && row[6] !== null) {
        fullPrice = typeof row[6] === 'number' ? row[6] : parseFloat(row[6])
        if (isNaN(fullPrice)) fullPrice = 0
      }
      
      let periodRevenueFormatted
      try {
        periodRevenueFormatted = new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(periodRevenue)
      } catch (error) {
        periodRevenueFormatted = `${currencySymbol}${periodRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      }
      
      return {
        id: planId,
        name: planName,
        accountName: accountName,
        periodRevenue: periodRevenue,
        periodRevenueFormatted: periodRevenueFormatted,
        price: fullPrice, // Full plan price
        start_timestamp: startTimestamp,
        end_timestamp: endTimestamp
      }
    })
    .filter(plan => plan !== null)
})

// Create a map of previous plans by ID for quick lookup
const previousPlansMap = computed(() => {
  const map = new Map()
  previousPlans.value.forEach(plan => {
    map.set(plan.id, plan)
  })
  return map
})

// Helper function to format date range
const formatDateRange = (startTimestamp, endTimestamp) => {
  if (!startTimestamp) return 'No start date'
  
  const startDate = new Date(startTimestamp * 1000)
  const startFormatted = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  
  if (!endTimestamp) {
    return `${startFormatted} - Ongoing`
  }
  
  const endDate = new Date(endTimestamp * 1000)
  const endFormatted = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  
  return `${startFormatted} - ${endFormatted}`
}

// Helper function to check if a timestamp falls within a period
const isTimestampInPeriod = (timestamp, periodStart, periodEnd) => {
  if (!timestamp) return false
  return timestamp >= periodStart && timestamp <= periodEnd
}

// Combine current and previous period data, calculate change based on plan start/end dates, and sort by biggest change
const plansWithChange = computed(() => {
  const currencySymbol = data.value?.analyticsQuery?.currency_symbol || '$'
  const rawLocale = data.value?.analyticsQuery?.locale || 'en-US'
  const locale = normalizeLocale(rawLocale)
  const currency = data.value?.analyticsQuery?.currency || 'USD'
  
  const plansMap = new Map()
  
  // Get current period date range
  const currentTimestamps = getDateRangeTimestamps(dateRange.value)
  const currentPeriodStart = currentTimestamps.from_timestamp
  const currentPeriodEnd = currentTimestamps.to_timestamp
  
  // Get previous period date range
  const previousTimestamps = getPreviousPeriodTimestamps(dateRange.value)
  const previousPeriodStart = previousTimestamps.from_timestamp
  const previousPeriodEnd = previousTimestamps.to_timestamp
  
  // Create a map of previous period plans for quick lookup
  const previousPlansById = new Map()
  previousPlans.value.forEach(prevPlan => {
    previousPlansById.set(prevPlan.id, prevPlan)
  })
  
  // Process current period plans
  plans.value.forEach(plan => {
    const prevPlan = previousPlansById.get(plan.id)
    
    // Check if plan started in current period
    const startedInCurrentPeriod = isTimestampInPeriod(plan.start_timestamp, currentPeriodStart, currentPeriodEnd)
    const startedInPreviousPeriod = prevPlan ? isTimestampInPeriod(prevPlan.start_timestamp, previousPeriodStart, previousPeriodEnd) : false
    
    // Check if plan ended in current period
    const endedInCurrentPeriod = plan.end_timestamp ? isTimestampInPeriod(plan.end_timestamp, currentPeriodStart, currentPeriodEnd) : false
    const wasActiveInPreviousPeriod = prevPlan !== undefined
    
    let change = 0
    let changeReason = null
    
    // New plan started this period (increase)
    if (startedInCurrentPeriod && !startedInPreviousPeriod) {
      change = plan.price // Full price as increase
      changeReason = 'new'
    }
    // Plan ended this period (decrease)
    else if (endedInCurrentPeriod && wasActiveInPreviousPeriod) {
      // Calculate decrease: plan went from full price to 0
      const previousPrice = prevPlan ? prevPlan.price : 0
      change = 0 - previousPrice // Negative change (full price decrease)
      changeReason = 'ended'
    }
    // Plan exists in both periods - compare prices (not revenue)
    else if (wasActiveInPreviousPeriod) {
      const previousPrice = prevPlan ? prevPlan.price : 0
      // Only show change if price actually changed
      if (Math.abs(plan.price - previousPrice) > 0.01) {
        change = plan.price - previousPrice
        changeReason = 'revenue_change'
      } else {
        change = 0
        changeReason = null
      }
    }
    
    plansMap.set(plan.id, {
      ...plan,
      // Display the full plan price, not proportional revenue
      currentPeriodRevenue: plan.price, // Use full plan price for display
      previousPeriodRevenue: prevPlan ? prevPlan.price : 0, // Use full plan price for comparison
      change: change,
      changeReason: changeReason,
      startedInCurrentPeriod: startedInCurrentPeriod,
      endedInCurrentPeriod: endedInCurrentPeriod,
      wasActiveInPreviousPeriod: wasActiveInPreviousPeriod
    })
  })
  
  // Format amounts and calculate absolute change for sorting
  const plansArray = Array.from(plansMap.values())
    .map(plan => {
      // Format current period revenue
      let thisPeriodFormatted
      try {
        thisPeriodFormatted = new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(plan.currentPeriodRevenue)
      } catch (error) {
        thisPeriodFormatted = `${currencySymbol}${plan.currentPeriodRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      }
      
      // Format date range
      const dateRangeFormatted = formatDateRange(plan.start_timestamp, plan.end_timestamp)
      
      // Format change amount
      let changeFormatted = null
      let changeType = 'neutral'
      const absChange = Math.abs(plan.change)
      
      // Show change for new plans, ended plans, or revenue changes
      if (absChange > 0.01 && (plan.changeReason === 'new' || plan.changeReason === 'ended' || plan.changeReason === 'revenue_change')) {
        try {
          const changeAmount = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(absChange)
          
          if (plan.changeReason === 'new') {
            changeFormatted = `New plan (+${changeAmount})`
            changeType = 'positive'
          } else if (plan.changeReason === 'ended') {
            changeFormatted = `Ended (${changeAmount})`
            changeType = 'negative'
          } else if (plan.change > 0) {
            changeFormatted = `Increased ${changeAmount}`
            changeType = 'positive'
          } else {
            changeFormatted = `Decreased ${changeAmount}`
            changeType = 'negative'
          }
        } catch (error) {
          const changeAmount = `${currencySymbol}${absChange.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          if (plan.changeReason === 'new') {
            changeFormatted = `New plan (+${changeAmount})`
            changeType = 'positive'
          } else if (plan.changeReason === 'ended') {
            changeFormatted = `Ended (${changeAmount})`
            changeType = 'negative'
          } else if (plan.change > 0) {
            changeFormatted = `Increased ${changeAmount}`
            changeType = 'positive'
          } else {
            changeFormatted = `Decreased ${changeAmount}`
            changeType = 'negative'
          }
        }
      }
      
      return {
        ...plan,
        thisPeriodFormatted,
        changeFormatted,
        changeType,
        absChange: absChange,
        dateRangeFormatted: dateRangeFormatted
      }
    })
  
  // Sort by absolute change (biggest change first), prioritizing new/ended plans, then by current period revenue
  return plansArray.sort((a, b) => {
    // Prioritize new plans and ended plans
    if (a.changeReason === 'new' && b.changeReason !== 'new') return -1
    if (b.changeReason === 'new' && a.changeReason !== 'new') return 1
    if (a.changeReason === 'ended' && b.changeReason !== 'ended') return -1
    if (b.changeReason === 'ended' && a.changeReason !== 'ended') return 1
    
    // Then sort by absolute change
    if (b.absChange !== a.absChange) {
      return b.absChange - a.absChange
    }
    return b.currentPeriodRevenue - a.currentPeriodRevenue
  })
})

// Calculate total revenue (sum of all period revenue for plans active in current period)
// Uses proportional revenue for accurate period totals
const totalRevenue = computed(() => {
  return plans.value.reduce((sum, plan) => sum + plan.periodRevenue, 0)
})

// Format total revenue
const totalRevenueFormatted = computed(() => {
  if (!data.value?.analyticsQuery) return '$0'
  
  const currencySymbol = data.value.analyticsQuery.currency_symbol || '$'
  const rawLocale = data.value.analyticsQuery.locale || 'en-US'
  const locale = normalizeLocale(rawLocale)
  const currency = data.value.analyticsQuery.currency || 'USD'
  
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(totalRevenue.value)
  } catch (error) {
    return `${currencySymbol}${totalRevenue.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
})

// Calculate previous period total revenue (sum of all period revenue for plans active in previous period)
const previousTotalRevenue = computed(() => {
  return previousPlans.value.reduce((sum, plan) => sum + plan.periodRevenue, 0)
})

// Format previous period total revenue
const previousTotalRevenueFormatted = computed(() => {
  if (!previousData.value?.analyticsQuery) return null
  
  const currencySymbol = previousData.value.analyticsQuery.currency_symbol || '$'
  const rawLocale = previousData.value.analyticsQuery.locale || 'en-US'
  const locale = normalizeLocale(rawLocale)
  const currency = previousData.value.analyticsQuery.currency || 'USD'
  
  if (previousTotalRevenue.value === 0) return null
  
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(previousTotalRevenue.value)
  } catch (error) {
    return `${currencySymbol}${previousTotalRevenue.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
})

const changePercentage = computed(() => {
  // Don't show comparison while data is loading
  if (loading.value || previousLoading.value) {
    return null
  }
  
  if (previousData.value === null || previousData.value === undefined) {
    return null
  }
  
  const current = totalRevenue.value
  const previous = previousTotalRevenue.value
  
  // If previous is 0 and current is also 0, don't show comparison
  if (previous === 0 && current === 0) return null
  
  // If previous is 0 but current has value, show as 100% increase
  if (previous === 0 && current > 0) {
    return 100
  }
  
  // Calculate percentage change
  const change = ((current - previous) / previous) * 100
  return Math.round(change * 10) / 10 // Round to 1 decimal place
})

// Open plan details when row is clicked
const { guard } = useTrialModeGuard()
const openPlanDetailsGuarded = guard((planId) => {
  if (!planId) return
  
  // Extract plan ID if it's a string with format like "plan-123"
  let accountPlanId = planId
  if (typeof planId === 'string' && planId.includes('-')) {
    const parts = planId.split('-')
    accountPlanId = parts[parts.length - 1]
  }
  
  
  // Use ViewDetails command format (matching other plan widgets)
  const message = {
    command: "ViewDetails",
    payload: {
      account_plan_id: accountPlanId,
      canvasUUID: "xxx" // Placeholder, parent will handle
    }
  }
  
  
  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(message, "*")
    } else {
      console.warn('⚠️ window.parent not available or same as window')
    }
  } catch (error) {
    console.error('❌ Error posting ViewDetails message:', error)
  }
})

const handleDateRangeChange = (newDateRange) => {
  if (newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
    emit('update:props', { dateRange: newDateRange })
  }
}

// Watch for prop changes from parent (e.g., when loading from database)
watch(() => props.props?.dateRange, (newDateRange) => {
  if (newDateRange && newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
  }
})

// Watch for date range changes and refresh data
watch(dateRange, () => {
  refresh(true)
})

onUnmounted(() => {
  cleanup()
  previousCleanup()
})
</script>

<style scoped>
.widget-content-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  overflow: hidden;
  gap: 0;
}

.total-revenue-metric {
  display: flex;
  flex-direction: column;
  margin-bottom: 0px;
  padding-bottom: 10px;
  flex-shrink: 0;
}

.metric-value-row {
  display: flex;
  align-items: baseline;
  gap: 0;
}

.metric-value-row .metric-value {
  margin-right: 8px;
}

.metric-value {
  font-size: 28px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.15;
  margin-top: -1px;
}

.metric-label {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  margin-top: 4px;
}

.loading-state,
.error-state {
  padding: 20px;
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
}

.error-state {
  color: var(--color-red);
}

.plans-table {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.table-header {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid #E0E0E0;
  margin-bottom: 8px;
}

.table-header-cell {
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.table-header-cell.plan-cell {
  text-align: left;
}

.table-header-cell.period-cell {
  text-align: right;
}

.table-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 16px;
  padding: 12px 0;
  min-height: 64px;
  border-bottom: 1px solid #F5F5F5;
  transition: background-color 0.2s ease;
}

.table-row:first-child {
  border-top: 1px solid #F5F5F5;
}

.table-row:last-child {
  border-bottom: none;
}

.table-row {
  cursor: pointer;
}

.table-row:hover {
  background-color: rgba(0, 0, 0, 0.02);
  border-radius: 4px;
  margin: 0 -4px;
  padding-left: 4px;
  padding-right: 4px;
}

.table-cell {
  display: flex;
  align-items: center;
}

.table-cell.plan-cell {
  justify-content: flex-start;
  min-width: 0;
}

.plan-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.plan-name {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.account-name {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.87);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.table-cell.period-cell {
  justify-content: flex-end;
  flex-shrink: 0;
}

.revenue-with-change {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.revenue-amount {
  font-size: 13px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  white-space: nowrap;
}

.change-indicator {
  font-size: 12px;
  font-weight: 400;
  white-space: nowrap;
}

.change-indicator.positive {
  color: #1C3829;
}

.change-indicator.negative {
  color: #402C1B;
}

.change-indicator.neutral {
  color: rgba(0, 0, 0, 0.87);
}

.no-plans {
  padding: 40px 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}
</style>
